import React, { useEffect } from "react";
import useStyles from "@hooks/useStyles";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import { Alert, AlertTitle } from "@material-ui/lab";
import useStores from "@hooks/useStores";
import { observer } from "mobx-react-lite";
import LoadingButton from "@components/Global/Custom/Button";
import StarIcon from "@material-ui/icons/Star";

import ReviewItem from "@custom/ReviewItem/ReviewItem";

import EmptyMessage from "@custom/EmptyMessage";
import ReviewsSearch from "./ReviewsSearch";
import { Link } from "@material-ui/core";

import { adjective as proAdjective } from "@language/provider";

const { BRAND_NAME, ENV } = process.env;

const ReviewsList = observer(() => {
	const stores = useStores();
	const { reviewsListStore: store } = stores;
	const classes = useStyles("Reviews");

	const handleLoadMore = async () => {
		await store.loadMore();
	};

	const openDialog = () => {
		stores.reviewsDialogStore.showModal = true;
	};

	const updateReview = (_id, toUpdate) => {
		store.updateReview(_id, toUpdate);
	};

	return (
		<Box
			component="main"
			minHeight={"calc(100vh - 144px)"}
			pt={4}
			px={{ xs: 2, sm: 6 }}
			pb={12}
		>
			<Container maxWidth={"lg"}>
				<Box
					mt={8}
					mb={8}
					display={"flex"}
					alignItems={{ xs: "flex-start", sm: "center" }}
					justifyContent={"space-between"}
					flexDirection={{ xs: "column", sm: "row" }}
				>
					<Box display={"flex"} flexDirection={"column"}>
						<Box display={"flex"} alignItems={"center"}>
							<Box fontWeight={600} fontSize={{ xs: 28, sm: 32 }} mr={4}>
								Reviews
							</Box>
						</Box>
					</Box>
					<Box display={"flex"} alignItems={"center"} mt={{ xs: 4, sm: 0 }}>
						{/* <Box mr={4}>
							<Button
								size={"large"}
								color={"secondary"}
								variant="outlined"
								//endIcon={<ExpandMoreIcon />}
							>
								Export
							</Button>
						</Box> */}
						<Button
							size={"large"}
							color={"primary"}
							variant={"contained"}
							disableElevation
							onClick={openDialog}
							//startIcon={<AddIcon />}
						>
							Send review invitation
						</Button>
					</Box>
				</Box>
				{ENV === "development" && <ReviewsSearch />}

				{ENV === "development" && (
					<Box mb={8}>
						<Paper variant={"outlined"}>
							<Box
								display={"flex"}
								alignItems={"center"}
								flexWrap={{ xs: "wrap", sm: "nowrap" }}
								p={6}
							>
								<Box
									flex={"1 1 auto"}
									mr={{ xs: 0, sm: 6 }}
									mb={{ xs: 4, sm: 0 }}
								>
									<Box display={"flex"} alignItems={"flex-start"}>
										<Box
											mr={6}
											minWidth={70}
											display={"flex"}
											alignItems={"center"}
										>
											<Box
												component={"img"}
												src="https://res.cloudinary.com/liaison-inc/image/upload/v1707014365/shared/google_business.jpg"
												width={70}
											/>
										</Box>
										<div>
											<Box
												fontWeight={700}
												mb={2}
												fontSize={{ xs: 18, sm: 20 }}
											>
												Connect your Google Business Profile
											</Box>
											<Box
												fontSize={16}
												color={"grey.700"}
												lineHeight={1.5}
												maxWidth={650}
											>
												{`Connecting takes just a few steps and is no extra cost. You'll be able to receive and respond to Google messages and reviews directly within ${BRAND_NAME}.`}{" "}
												<Link color="primary" href="xxx" target="_blank">
													Learn more
												</Link>
											</Box>
										</div>
									</Box>
								</Box>

								<Box
									width={{ xs: "100%", sm: "auto" }}
									display={"flex"}
									alignItems={"center"}
									height={"100%"}
									flexShrink={0}
									ml={{ xs: "94px", sm: 0 }}
								>
									<Button
										disableElevation
										size={"large"}
										color="primary"
										variant={"contained"}
										onClick={openDialog}
									>
										Connect
									</Button>
								</Box>
							</Box>
						</Paper>
					</Box>
				)}

				{!!(store.reviews.length < 5 && store.reviews.length > 0) && (
					<Box mb={8}>
						<Paper variant={"outlined"}>
							<Box
								display={"flex"}
								alignItems={"center"}
								flexWrap={{ xs: "wrap", sm: "nowrap" }}
								p={6}
							>
								<Box
									flex={"1 1 auto"}
									mr={{ xs: 0, sm: 6 }}
									mb={{ xs: 4, sm: 0 }}
								>
									<Box display={"flex"} alignItems={"flex-start"}>
										<Box
											mr={2}
											minWidth={40}
											display={"flex"}
											alignItems={"center"}
										>
											<StarIcon style={{ color: "#fcd53f", fontSize: 36 }} />
										</Box>
										<div>
											<Box
												fontWeight={700}
												mb={2}
												fontSize={{ xs: 18, sm: 20 }}
											>
												{proAdjective.plural.ucFirst()} with 5 or more reviews
												get the most customers
											</Box>
											<Box fontSize={16} color={"grey.700"}>
												Ask past customers to write a review for you on{" "}
												{BRAND_NAME}.
											</Box>
										</div>
									</Box>
								</Box>

								<Box
									width={{ xs: "100%", sm: "auto" }}
									display={"flex"}
									alignItems={"center"}
									height={"100%"}
									flexShrink={0}
									ml={{ xs: 12 }}
								>
									<Button
										disableElevation
										size={"large"}
										color="primary"
										variant={"contained"}
										onClick={openDialog}
									>
										Ask for reviews
									</Button>
								</Box>
							</Box>
						</Paper>
					</Box>
				)}

				<Box pt={4} pb={6}>
					<Divider light={true} />
				</Box>

				{!!store.reviews.length &&
					store.reviews.map((review, index, arr) => {
						return (
							<React.Fragment key={review._id}>
								<ReviewItem
									key={review._id}
									review={review}
									displayReplyName={false}
									onSubmitResponse={updateReview}
								/>

								{index !== arr.length - 1 && (
									<div className={classes.reviewDivider}>
										<Divider light={true} />
									</div>
								)}
							</React.Fragment>
						);
					})}

				{store.hasMore && (
					<Box mb={6} mt={12} display={"flex"} justifyContent={"center"}>
						<LoadingButton
							disableElevation
							size={"large"}
							variant={"outlined"}
							color={"default"}
							label={"See more"}
							onClick={handleLoadMore}
							style={{ backgroundColor: "#FFF" }}
						/>
					</Box>
				)}

				{!store.reviews.length && (
					<Box my={{ xs: 10, sm: 20 }}>
						<EmptyMessage
							header={"You have no reviews."}
							body={`Reviews build trust and boost your chances of getting hired significantly. Reviews show new customers how excited people are about working with you. Ask past customers to write a review for you with the link below.`}
							variant={"reviews"}
						/>
						<Box textAlign={"center"} mt={8}>
							<Button
								disableElevation
								size={"large"}
								color="primary"
								variant={"contained"}
								onClick={openDialog}
							>
								Request reviews
							</Button>
						</Box>
					</Box>
				)}
			</Container>
		</Box>
	);
});

export default ReviewsList;
